import { useState, useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import type { Product } from '../../types'
import { formatRupiah } from '../../types'
import { productApi, dashboardApi } from '../../utils/api'
import type { DashboardRecentOrder as RecentOrder } from '../../utils/api'

interface DashboardStats {
  totalProducts: number
  activeProducts: number
  todayOrders: number
  todayTotalAmount: number
}

export function AdminDashboardPage() {
  const [products, setProducts] = useState<Product[]>([])
  const [stats, setStats] = useState<DashboardStats>({
    totalProducts: 0,
    activeProducts: 0,
    todayOrders: 0,
    todayTotalAmount: 0,
  })
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Product list (for the "Produk Terbaru" table) and dashboard
      // statistics (counts, sums, recent orders) both come from the backend.
      const [productsRes, statsRes] = await Promise.all([
        productApi.getAll(),
        dashboardApi.getStats(),
      ])
      const productsData = productsRes.data as Product[]
      setProducts(productsData)

      setStats({
        totalProducts: statsRes.data.totalProductCount,
        activeProducts: statsRes.data.activeProductCount,
        todayOrders: statsRes.data.todayOrderCount,
        todayTotalAmount: statsRes.data.todayTransactionAmount,
      })

      setRecentOrders(statsRes.data.recentOrders)
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err)
      setError('Gagal memuat data dashboard')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-60vh">
        <div className="loading-spinner-flat" aria-label="Memuat dashboard" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="error-state-flat">
        <svg className="w-16 h-16 text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h2 className="text-xl font-bold text-slate-900 mb-2">Gagal Memuat Dashboard</h2>
        <p className="text-sm text-slate-500 mb-6">{error}</p>
        <button onClick={fetchDashboardData} className="flat-btn flat-btn-primary">
          Coba Lagi
        </button>
      </div>
    )
  }

  return (
    <div className="dashboard-shell">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Dashboard</h1>
        <p className="dashboard-subtitle">Ringkasan toko dan aktivitas</p>
        <div className="dashboard-accent-bar" aria-hidden="true" />
      </div>

      <div className="stat-grid">
        <div className="flat-stat-card stat-tint-blue">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="stat-label">Total Produk Aktif</h3>
              <p className="stat-value">{stats.activeProducts}</p>
            </div>
            <div className="flat-icon-container stat-chip">
              <svg className="w-6 h-6 icon-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="flat-stat-card stat-tint-blue">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="stat-label">Total Semua Produk</h3>
              <p className="stat-value">{stats.totalProducts}</p>
            </div>
            <div className="flat-icon-container stat-chip">
              <svg className="w-6 h-6 icon-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
          </div>
        </div>

        <div className="flat-stat-card stat-tint-blue">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="stat-label">Pesanan Hari Ini</h3>
              <p className="stat-value">{stats.todayOrders}</p>
            </div>
            <div className="flat-icon-container stat-chip">
              <svg className="w-6 h-6 icon-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
          </div>
        </div>

        <div className="flat-stat-card stat-solid-blue">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="stat-label">Total Nilai Pesanan Hari Ini</h3>
              <p className="stat-value">{formatRupiah(stats.todayTotalAmount)}</p>
            </div>
            <div className="flat-icon-container stat-chip-solid">
              <svg className="w-6 h-6 icon-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="content-grid">
        <div className="card-flat">
          <div className="section-header-flat">
            <h2 className="section-title-flat">Aksi Cepat</h2>
          </div>
          <div className="quick-action-grid">
            <NavLink to="/admin/products" className="quick-action-btn quick-action-btn-primary">
              <span className="quick-action-icon" aria-hidden="true">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </span>
              <span className="quick-action-text">
                <span className="quick-action-title">Tambah Produk</span>
                <span className="quick-action-desc">Buat produk baru</span>
              </span>
            </NavLink>
            <NavLink to="/admin/products" className="quick-action-btn">
              <span className="quick-action-icon" aria-hidden="true">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </span>
              <span className="quick-action-text">
                <span className="quick-action-title">Kelola Produk</span>
                <span className="quick-action-desc">Ubah harga & stok</span>
              </span>
            </NavLink>
            <NavLink to="/admin/orders" className="quick-action-btn">
              <span className="quick-action-icon" aria-hidden="true">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </span>
              <span className="quick-action-text">
                <span className="quick-action-title">Lihat Pesanan</span>
                <span className="quick-action-desc">Pantau pesanan</span>
              </span>
            </NavLink>
          </div>
        </div>

        <div className="card-flat">
          <div className="section-header-flat">
            <h2 className="section-title-flat">Info</h2>
          </div>
          <div className="p-4 space-y-3">
            <div className="flat-info-box info-blue">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 info-icon flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-slate-900">Total Produk: {stats.totalProducts}</p>
                  <p className="text-sm text-slate-500 mt-1">Aktif: {stats.activeProducts} | Non-aktif: {stats.totalProducts - stats.activeProducts}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="two-col-grid">
        <div className="card-flat">
          <div className="section-header-flat">
            <h2 className="section-title-flat">Produk Terbaru</h2>
            <NavLink to="/admin/products" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              Lihat Semua
            </NavLink>
          </div>
          <div className="overflow-x-auto">
            <table className="table-flat table-tint-blue">
              <thead>
                <tr>
                  <th>Produk</th>
                  <th>Kategori</th>
                  <th>Harga PCS</th>
                  <th>Harga Pack</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {products.slice(0, 5).length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-slate-500 py-6">
                      Belum ada produk
                    </td>
                  </tr>
                ) : (
                  products.slice(0, 5).map((product) => (
                    <tr key={product.id}>
                      <td>
                        <p className="font-medium text-slate-900">{product.name}</p>
                      </td>
                      <td className="text-slate-500">{product.category || '-'}</td>
                      <td>
                        {product.pricePcs ? formatRupiah(product.pricePcs) : '-'}
                      </td>
                      <td>
                        {product.pricePack ? formatRupiah(product.pricePack) : '-'}
                      </td>
                      <td>
                        <span className="badge-flat badge-blue">
                          {product.active ? 'Aktif' : 'Non-Aktif'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card-flat">
          <div className="section-header-flat">
            <h2 className="section-title-flat">Pesanan Terbaru</h2>
            <NavLink to="/admin/orders" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              Lihat Semua
            </NavLink>
          </div>
          <div className="p-0">
            {recentOrders.length === 0 ? (
              <div className="empty-state-flat">
                <svg className="w-12 h-12 text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                <p className="text-sm text-slate-500">Belum ada aktivitas pesanan</p>
                <p className="text-xs text-slate-400 mt-1">Pesanan pelanggan terbaru akan muncul di sini.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table-flat table-tint-blue">
                  <thead>
                    <tr>
                      <th>No. Pesanan</th>
                      <th>Outlet</th>
                      <th>Total</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.map((order) => (
                      <tr key={order.id}>
                        <td className="font-mono text-sm">{order.orderNumber}</td>
                        <td className="font-medium text-slate-900">{order.outletName}</td>
                        <td className="font-semibold">{formatRupiah(order.totalAmount)}</td>
                        <td>
                          <span className="badge-flat badge-blue">{order.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}